import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

let _setGlobalModal: ((node: ReactNode | null) => void) | null = null;

/** Show or hide a modal globally (immediate call) */
export const setGlobalModal = (node: ReactNode | null) => {
  _setGlobalModal?.(node);
};

/**
 * Safe version to open a modal *even if component unmounts instantly*,
 * e.g. inside a dropdown or popover. It defers to the next frame.
 *
 * Wraps the node in a div to avoid ref forwarding issues with Radix UI components.
 */
export const safelyOpenGlobalModal = (node: ReactNode) => {
  requestAnimationFrame(() => {
    // Wrap in a fragment to ensure clean rendering and avoid ref issues
    _setGlobalModal?.(<>{node}</>);
  });
};

/** Root renderer mounted once in App.tsx */
export const GlobalModalRoot: React.FC = () => {
  const [node, setNode] = useState<ReactNode | null>(null);

  useEffect(() => {
    _setGlobalModal = setNode;
    return () => {
      _setGlobalModal = null;
    };
  }, []);

  const container =
    typeof document !== 'undefined'
      ? document.getElementById('global-modal-root')
      : null;

  if (!node || !container) return null;

  // Wrap in a div to provide a stable container and avoid ref forwarding warnings
  return createPortal(
    <div style={{ display: 'contents' }}>
      {node}
    </div>,
    container
  );
};
